import { DEG } from '@/core/math/scalar';
import { EngineDef, ShipDef } from '../ShipDef';

/**
 * Spacecraft definitions.
 *
 * Everything about a vessel's behaviour comes from this data: thrust, Isp,
 * engine placement (which determines the torque an off-axis engine produces),
 * tank positions (which determine how the centre of mass shifts as propellant
 * drains), aerodynamic shape, structural limits and thermal capacity.
 *
 * Adding a ship class means adding an object here.
 */

/** Builds the eight-thruster RCS block used by most hulls. */
function rcsCluster(prefix: string, thrust: number, arm: number, lever: number): EngineDef[] {
  const common = {
    type: 'rcs' as const,
    maxThrust: thrust,
    ispVacuum: 240,
    ispSeaLevel: 220,
    fuelType: 'monopropellant',
    powerDraw: 120,
    heatOutput: 400,
    minThrottle: 0,
    throttleRate: 12,
    mass: 6,
    gimbalRange: 0,
    attitudeCapable: true,
  };
  const mounts: { id: string; pos: [number, number, number]; dir: [number, number, number] }[] = [
    { id: 'fwd-up', pos: [0, arm, lever], dir: [0, 1, 0] },
    { id: 'fwd-down', pos: [0, -arm, lever], dir: [0, -1, 0] },
    { id: 'fwd-left', pos: [-arm, 0, lever], dir: [-1, 0, 0] },
    { id: 'fwd-right', pos: [arm, 0, lever], dir: [1, 0, 0] },
    { id: 'aft-up', pos: [0, arm, -lever], dir: [0, 1, 0] },
    { id: 'aft-down', pos: [0, -arm, -lever], dir: [0, -1, 0] },
    { id: 'aft-left', pos: [-arm, 0, -lever], dir: [-1, 0, 0] },
    { id: 'aft-right', pos: [arm, 0, -lever], dir: [1, 0, 0] },
    { id: 'roll-a', pos: [arm, arm, 0], dir: [-1, 1, 0] },
    { id: 'roll-b', pos: [-arm, -arm, 0], dir: [1, -1, 0] },
    { id: 'retro-a', pos: [arm, 0, lever * 0.6], dir: [0, 0, -1] },
    { id: 'retro-b', pos: [-arm, 0, lever * 0.6], dir: [0, 0, -1] },
  ];
  return mounts.map((m) => ({
    ...common,
    id: `${prefix}-${m.id}`,
    name: `RCS ${m.id}`,
    position: m.pos,
    direction: m.dir,
    description: 'Cold-gas reaction control thruster.',
  }));
}

// ---------------------------------------------------------------------------
// Kestrel: the starter vessel. Small, forgiving, atmosphere-capable.
// ---------------------------------------------------------------------------
const KESTREL: ShipDef = {
  id: 'kestrel',
  name: 'Kestrel',
  className: 'Light Survey Lander',
  structureMass: 4200,
  size: [7.2, 4.4, 14.6],
  cargoCapacity: 3000,
  crew: 2,
  engines: [
    {
      id: 'main',
      name: 'RL-9 Orbital Main',
      type: 'chemical',
      maxThrust: 128000,
      ispVacuum: 342,
      ispSeaLevel: 286,
      fuelType: 'rocketFuel',
      powerDraw: 4200,
      heatOutput: 96000,
      minThrottle: 0.35,
      throttleRate: 1.8,
      mass: 480,
      position: [0, 0, -6.6],
      direction: [0, 0, 1],
      gimbalRange: 6 * DEG,
      attitudeCapable: true,
      description:
        'Pressure-fed bipropellant main engine. Gimballed, deep-throttle limited, and the only engine with ' +
        'enough thrust for orbital insertion.',
    },
    {
      id: 'turbine-port',
      name: 'AT-4 Atmospheric Turbine (port)',
      type: 'turbine',
      maxThrust: 46000,
      ispVacuum: 0,
      ispSeaLevel: 2900,
      fuelType: 'rocketFuel',
      powerDraw: 2200,
      heatOutput: 28000,
      minThrottle: 0.1,
      throttleRate: 2.4,
      mass: 320,
      position: [-2.6, -0.3, -1.2],
      direction: [0, 0, 1],
      gimbalRange: 3 * DEG,
      airBreathingReferenceDensity: 1.2,
      attitudeCapable: false,
      description:
        'Air-breathing turbine. Extremely efficient inside a dense atmosphere and completely useless outside one.',
    },
    {
      id: 'turbine-starboard',
      name: 'AT-4 Atmospheric Turbine (starboard)',
      type: 'turbine',
      maxThrust: 46000,
      ispVacuum: 0,
      ispSeaLevel: 2900,
      fuelType: 'rocketFuel',
      powerDraw: 2200,
      heatOutput: 28000,
      minThrottle: 0.1,
      throttleRate: 2.4,
      mass: 320,
      position: [2.6, -0.3, -1.2],
      direction: [0, 0, 1],
      gimbalRange: 3 * DEG,
      airBreathingReferenceDensity: 1.2,
      attitudeCapable: false,
      description: 'Air-breathing turbine, starboard nacelle.',
    },
    {
      id: 'landing-fwd',
      name: 'Landing Thruster (forward)',
      type: 'landing',
      maxThrust: 22000,
      ispVacuum: 268,
      ispSeaLevel: 240,
      fuelType: 'rocketFuel',
      powerDraw: 800,
      heatOutput: 14000,
      minThrottle: 0,
      throttleRate: 6,
      mass: 90,
      position: [0, -1.6, 3.4],
      direction: [0, -1, 0],
      gimbalRange: 0,
      attitudeCapable: true,
      description: 'Downward-firing hover and touchdown thruster.',
    },
    {
      id: 'landing-aft',
      name: 'Landing Thruster (aft)',
      type: 'landing',
      maxThrust: 22000,
      ispVacuum: 268,
      ispSeaLevel: 240,
      fuelType: 'rocketFuel',
      powerDraw: 800,
      heatOutput: 14000,
      minThrottle: 0,
      throttleRate: 6,
      mass: 90,
      position: [0, -1.6, -3.4],
      direction: [0, -1, 0],
      gimbalRange: 0,
      attitudeCapable: true,
      description: 'Downward-firing hover and touchdown thruster.',
    },
    ...rcsCluster('rcs', 1400, 2.1, 5.4),
  ],
  tanks: [
    { id: 'main-fwd', fuelType: 'rocketFuel', capacity: 5200, position: [0, 0, 2.2], dryMass: 210 },
    { id: 'main-aft', fuelType: 'rocketFuel', capacity: 5200, position: [0, 0, -2.6], dryMass: 210 },
    { id: 'mono', fuelType: 'monopropellant', capacity: 420, position: [0, 1.2, 3.8], dryMass: 40 },
    // Reactor fuel. Without a tank for it the reactor never starts, and the
    // vessel is left running on solar and battery alone.
    { id: 'reactor-core', fuelType: 'enrichedFuel', capacity: 60, position: [0, 0.6, -2.6], dryMass: 120 },
  ],
  fieldDrive: {
    id: 'faad-mk1',
    name: 'FAAD Mk.I Field-Assisted Ascent Drive',
    maxAcceleration: 11.5,
    referenceGravity: 9.81,
    minEfficiency: 0.04,
    maxEfficiency: 1.35,
    // Power cost is sized against what the vessel can actually generate: full
    // output draws roughly 670 kW, which the reactor plus a battery discharge
    // can sustain for tens of minutes but not indefinitely. Making it any more
    // expensive would render the drive decorative.
    powerPerNewton: 3.2,
    heatFraction: 0.34,
    thermalLimit: 780,
    thermalCutoff: 980,
    // Sized so continuous full output passes the derating threshold in about
    // two minutes and a similar pause restores it: long enough for an ascent
    // burn, short enough that indefinite hovering is not free.
    heatCapacity: 5.6e4,
    coolingCoefficient: 240,
    mass: 940,
    description:
      'Generates a local acceleration field opposing the dominant gravity vector. Efficiency scales with the ' +
      'strength of that field, so it is powerful on a surface and nearly useless in deep space. It consumes ' +
      'large amounts of electrical power and must be allowed to cool between sustained burns.',
  },
  powerPlants: [
    {
      id: 'reactor',
      name: 'CX-2 Compact Reactor',
      output: 420000,
      heatOutput: 90000,
      fuelRate: 0.00004,
      fuelType: 'enrichedFuel',
      solar: false,
      mass: 680,
    },
    {
      id: 'solar',
      name: 'Deployable Solar Array',
      output: 0,
      heatOutput: 0,
      fuelRate: 0,
      fuelType: null,
      solar: true,
      area: 34,
      mass: 120,
    },
  ],
  batteries: [{ id: 'main-bank', capacity: 1.4e9, maxDischarge: 1.6e6, mass: 340 }],
  radiators: [{ id: 'primary', area: 42, emissivity: 0.92, deployable: true, mass: 180 }],
  landingGear: {
    contacts: [
      [-2.8, -2.3, 3.1],
      [2.8, -2.3, 3.1],
      [-2.8, -2.3, -3.9],
      [2.8, -2.3, -3.9],
    ],
    maxImpactSpeed: 6.5,
    stiffness: 240000,
    damping: 42000,
    friction: 0.85,
  },
  aero: {
    frontalArea: 11.4,
    broadsideArea: 74,
    dragCoefficientAxial: 0.32,
    dragCoefficientBroadside: 1.28,
    wingArea: 26,
    liftSlope: 3.4,
    stallAngle: 17 * DEG,
    staticMargin: 1.9,
    referenceLength: 14.6,
    controlSurfaceArea: 6.2,
  },
  hullHeatCapacity: 5.4e6,
  hullTemperatureLimit: 1450,
  structuralLimitG: 9,
  maxDynamicPressure: 62000,
  reactionWheelTorque: 9000,
  reactionWheelPower: 6400,
  description:
    'A twenty-year-old light survey lander with a hybrid propulsion fit: turbines for atmospheric cruise, a ' +
    'single gimballed main engine for orbital work, landing thrusters for the last hundred metres, and a first ' +
    'generation field drive that makes repeated ascents from a terrestrial world merely expensive rather than ' +
    'impossible.',
};

// ---------------------------------------------------------------------------
// Drayman: heavy cargo hauler. Poor in atmosphere, excellent between planets.
// ---------------------------------------------------------------------------
const DRAYMAN: ShipDef = {
  id: 'drayman',
  name: 'Drayman',
  className: 'Heavy Cargo Hauler',
  structureMass: 26000,
  size: [12.0, 11.0, 38.0],
  cargoCapacity: 90000,
  crew: 4,
  engines: [
    {
      id: 'main-a',
      name: 'NT-40 Nuclear Thermal (A)',
      type: 'nuclearThermal',
      maxThrust: 420000,
      ispVacuum: 880,
      ispSeaLevel: 610,
      fuelType: 'hydrogen',
      powerDraw: 18000,
      heatOutput: 460000,
      minThrottle: 0.25,
      throttleRate: 0.7,
      mass: 3400,
      position: [-2.4, 0, -17.0],
      direction: [0, 0, 1],
      gimbalRange: 4 * DEG,
      attitudeCapable: true,
      description: 'Nuclear thermal rocket. High thrust and high efficiency, but slow to throttle and very hot.',
    },
    {
      id: 'main-b',
      name: 'NT-40 Nuclear Thermal (B)',
      type: 'nuclearThermal',
      maxThrust: 420000,
      ispVacuum: 880,
      ispSeaLevel: 610,
      fuelType: 'hydrogen',
      powerDraw: 18000,
      heatOutput: 460000,
      minThrottle: 0.25,
      throttleRate: 0.7,
      mass: 3400,
      position: [2.4, 0, -17.0],
      direction: [0, 0, 1],
      gimbalRange: 4 * DEG,
      attitudeCapable: true,
      description: 'Nuclear thermal rocket, starboard.',
    },
    {
      id: 'ion-array',
      name: 'XI-12 Ion Array',
      type: 'ion',
      maxThrust: 5600,
      ispVacuum: 4200,
      ispSeaLevel: 4200,
      fuelType: 'xenon',
      powerDraw: 340000,
      heatOutput: 120000,
      minThrottle: 0.02,
      throttleRate: 0.4,
      mass: 1800,
      position: [0, 0, -16.0],
      direction: [0, 0, 1],
      gimbalRange: 0,
      attitudeCapable: false,
      description: 'Gridded ion array. Almost no thrust, extraordinary efficiency, enormous power draw.',
    },
    ...rcsCluster('rcs', 6200, 5.2, 15.0),
  ],
  tanks: [
    { id: 'h2-a', fuelType: 'hydrogen', capacity: 62000, position: [0, 0, 6.0], dryMass: 3400 },
    { id: 'h2-b', fuelType: 'hydrogen', capacity: 62000, position: [0, 0, -6.0], dryMass: 3400 },
    { id: 'xe', fuelType: 'xenon', capacity: 9000, position: [0, -2.0, -11.0], dryMass: 700 },
    { id: 'mono', fuelType: 'monopropellant', capacity: 2400, position: [0, 3.0, 10.0], dryMass: 220 },
    { id: 'reactor-core', fuelType: 'enrichedFuel', capacity: 420, position: [0, 1.0, -13.0], dryMass: 900 },
  ],
  fieldDrive: {
    id: 'faad-mk3',
    name: 'FAAD Mk.III Heavy Field Drive',
    maxAcceleration: 8.2,
    referenceGravity: 9.81,
    minEfficiency: 0.03,
    maxEfficiency: 1.15,
    powerPerNewton: 3.8,
    heatFraction: 0.38,
    thermalLimit: 840,
    thermalCutoff: 1050,
    heatCapacity: 3.9e5,
    coolingCoefficient: 1700,
    mass: 6200,
    description: 'Scaled-up field drive sized for a loaded hauler. Cannot lift the ship alone above about 1.1 g.',
  },
  powerPlants: [
    {
      id: 'reactor',
      name: 'FR-9 Fission Reactor',
      output: 3.2e6,
      heatOutput: 520000,
      fuelRate: 0.00021,
      fuelType: 'enrichedFuel',
      solar: false,
      mass: 4800,
    },
  ],
  batteries: [{ id: 'bank-a', capacity: 9.4e9, maxDischarge: 6.0e6, mass: 2600 }],
  radiators: [
    { id: 'primary', area: 260, emissivity: 0.94, deployable: true, mass: 1400 },
    { id: 'secondary', area: 180, emissivity: 0.94, deployable: true, mass: 980 },
  ],
  landingGear: {
    contacts: [
      [-5.4, -5.6, 12.0],
      [5.4, -5.6, 12.0],
      [-5.4, -5.6, -12.0],
      [5.4, -5.6, -12.0],
    ],
    maxImpactSpeed: 3.2,
    stiffness: 1.8e6,
    damping: 320000,
    friction: 0.78,
  },
  aero: {
    frontalArea: 96,
    broadsideArea: 420,
    dragCoefficientAxial: 0.58,
    dragCoefficientBroadside: 1.42,
    wingArea: 0,
    liftSlope: 0,
    stallAngle: 0,
    staticMargin: 3.4,
    referenceLength: 38,
    controlSurfaceArea: 0,
  },
  hullHeatCapacity: 4.2e7,
  hullTemperatureLimit: 1180,
  structuralLimitG: 4,
  maxDynamicPressure: 24000,
  reactionWheelTorque: 62000,
  reactionWheelPower: 42000,
  description:
    'An unglamorous interplanetary freighter. No wings, no grace, and a structural limit that makes atmospheric ' +
    'entry a genuinely bad idea on anything denser than Kharis. What it does have is ninety tonnes of hold and ' +
    'the delta-v to cross the system with it.',
};

/**
 * How much longer a tank lasts here than it would with real chemistry.
 *
 * This build is an exploration demo: running out of propellant three hundred
 * kilometres from anywhere is not a thing it wants to be about. Raising tank
 * *capacity* cannot buy that, because propellant mass is real — fifty times
 * the Kestrel's 10.9 t of propellant is a 500 t lander that its 264 kN of
 * thrust cannot move, let alone fly. What is scaled instead is efficiency:
 * every engine's specific impulse and every reactor's burn rate, which is the
 * one term that changes endurance without touching mass, thrust, or how the
 * vessel handles.
 *
 * Nothing downstream is special-cased. Mass still falls as propellant burns,
 * the mass model still shifts the centre of mass as individual tanks drain,
 * the rocket equation still governs delta-v, and the propellant gauge still
 * reads real tank state — it just takes about forty-five minutes of
 * continuous full-thrust main-engine burn to empty the Kestrel instead of
 * four minutes.
 *
 * Isp is scaled on both the vacuum and sea-level figures, so their *ratio* is
 * untouched and pressure-dependent thrust (see 'Engine.maxThrustAt') behaves
 * exactly as before. Kept modest (rather than higher) so the delta-v readout
 * stays in a plausible range instead of reading as absurd.
 */
const ENDURANCE_SCALE = 10;

function withEnduranceScale(def: ShipDef): ShipDef {
  return {
    ...def,
    engines: def.engines.map((e) => ({
      ...e,
      ispVacuum: e.ispVacuum * ENDURANCE_SCALE,
      ispSeaLevel: e.ispSeaLevel * ENDURANCE_SCALE,
    })),
    powerPlants: def.powerPlants.map((pp) => ({ ...pp, fuelRate: pp.fuelRate / ENDURANCE_SCALE })),
  };
}

/**
 * The definitions the game actually flies. Only this list is exported: a hull
 * read straight from its literal above would be the unscaled version, and two
 * different Kestrels in one build is exactly the sort of thing that gets found
 * from a confusing capture rather than from the source.
 */
export const SHIPS: ShipDef[] = [KESTREL, DRAYMAN].map(withEnduranceScale);

export const STARTER_SHIP_ID = 'kestrel';

export function findShipDef(id: string): ShipDef {
  const s = SHIPS.find((x) => x.id === id);
  if (!s) throw new Error(`Unknown ship definition "${id}"`);
  return s;
}
